import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { DaySource } from './day-assignments';

// ── Tipler ─────────────────────────────────────────────────────────────────

export type PanelSource = 'ABANT_PANEL' | 'HAYAT_PANEL';

/** Listeleme `orders[].reason` alanında dönebilecek kodlar. */
export type OrderReason =
  'NO_LABEL' | 'UNMAPPED' | 'NO_VEHICLE' | 'DRIVER_INACTIVE' | 'VEHICLE_BUSY';

/** Aracın bugünkü hedefi. `source`: aracın günün şoförü hangi katmandan (sefer/görevlendirme/zimmet). */
export interface PanelAssignTarget {
  vehicleId: string;
  plate: string;
  tripId: string | null;
  tripStatus: string | null;
  source: DaySource | null;
}

/** Hayat etiketi nasıl çözüldü: plaka (`PLATE`), geçiş takma adı (`ALIAS`), çözülemedi (`null`). */
export type PanelAssignVia = 'PLATE' | 'ALIAS' | null;

export interface PanelAssignLabel {
  label: string;
  displayName: string | null;
  /** Yalnız Abant; Hayat'ta her zaman null. */
  driver: { id: string; name: string } | null;
  target: PanelAssignTarget | null;
  /** Yalnız Hayat; Abant'ta null. */
  via: PanelAssignVia;
  activeCount: number;
}

export interface PanelAssignOrder {
  orderId: string;
  externalId: string | null;
  label: string | null;
  neighborhood: string | null;
  receivedAt: string;
  status: string;
  target: PanelAssignTarget | null;
  reason: OrderReason | null;
}

export interface PanelAssignDriver {
  id: string;
  name: string;
  /** Şoförün bugünkü aracı (roster). Aracı yoksa null. */
  vehiclePlate: string | null;
  vehicleSource: DaySource | null;
}

export interface PanelAssignResponse {
  source: PanelSource;
  labels: PanelAssignLabel[];
  orders: PanelAssignOrder[];
  drivers: PanelAssignDriver[];
}

/** `PUT driver-map` yanıtı: güncel liste + yeniden eşleme düştüyse `rematchFailed`. */
export type PanelAssignSaveResult = PanelAssignResponse & { rematchFailed: boolean };

// ── Sorgu ───────────────────────────────────────────────────────────────────

export function usePanelAssign(source: PanelSource | null) {
  return useQuery({
    queryKey: ['panel-assign', source],
    queryFn: () => api.get<PanelAssignResponse>(`/api/panel-assign?source=${source}`),
    enabled: source !== null,
  });
}

// ── Eşleme kaydetme ────────────────────────────────────────────────────────

export function useSaveDriverMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { source: PanelSource; map: Record<string, string | null> }) =>
      api.put<PanelAssignSaveResult>(`/api/panel-assign/driver-map`, v),
    onSuccess: (_data, v) => {
      void qc.invalidateQueries({ queryKey: ['panel-assign', v.source] });
    },
  });
}
